/* eslint-disable no-prototype-builtins */
/* eslint-disable no-restricted-syntax */
class Performance {
  constructor(props) {
    this.state = {
      hasPerformanceNavigation: (global.window && window.hasOwnProperty('PerformanceNavigationTiming')) || false,
      isPerformanceEnabled: (global.window && window.hasOwnProperty('performance')) || false,
      perfEventsRequired: this.getPerfEvents(props),
      pollingInterval: props.pollingInterval || 2000,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  getPerfEvents(props) {
    const { perfEvents } = props;
    if (perfEvents && perfEvents.length > 0) {
      return perfEvents;
    }
    return [];
  }

  // eslint-disable-next-line class-methods-use-this
  getRoundedNumber(val) {
    return Math.round(val);
  }

  getBaseEvents() {
    const { isPerformanceEnabled } = this.state;
    if (!isPerformanceEnabled) {
      return {};
    }

    const [time] = performance.getEntriesByType('navigation');
    const getFullNumber = this.getRoundedNumber;
    const {
      connectEnd, connectStart, domainLookupEnd, domainLookupStart, domComplete,
      domContentLoadedEventEnd, domInteractive, duration, fetchStart, loadEventEnd,
      redirectEnd, redirectStart, requestStart, responseEnd, responseStart,
      secureConnectionStart, unloadEventEnd, unloadEventStart, workerStart,
    } = time;

    return {
      // total connection time
      connectionTime: getFullNumber(connectEnd - connectStart),
      // DNS Lookup Time
      dnsTime: getFullNumber(domainLookupEnd - domainLookupStart),
      // the page and all of its subresources are ready.
      domComplete: getFullNumber(domComplete),
      /*
       * marks the point when both the DOM is ready and there are no stylesheets
       * that are blocking JavaScript execution - construct the render tree
       */
      domContentLoaded: getFullNumber(domContentLoadedEventEnd),
      // DOM is ready.
      domInteractive: getFullNumber(domInteractive),
      // Response time only (download)
      downloadTime: getFullNumber(responseEnd - responseStart),
      // resources have completely finished loading
      duration: getFullNumber(duration),
      /* fetchStart is a recorded moment in time where the browser
       *  first attempts to retrieve a document
       */
      fetchStart: getFullNumber(fetchStart),
      // Cache seek plus response time
      fetchTime: getFullNumber(responseEnd - fetchStart),
      // check against navigation obj loaded
      loadEventEnd: getFullNumber(loadEventEnd),
      // redirection duration
      redirectTime: getFullNumber(redirectEnd - redirectStart),
      /* time immediately before the browser starts requesting the resource from
       * the server, cache, or local resource
       */
      requestStart: getFullNumber(requestStart),
      // secure connection time in total connection time
      secureConnectionTime: secureConnectionStart > 0
        ? getFullNumber(connectEnd - secureConnectionStart) : 0,
      // Request plus response time (network only)
      totalNetworkTime: getFullNumber(responseEnd - requestStart),
      // Time to First Byte
      ttfb: getFullNumber(responseStart - requestStart),
      // unload time duration
      unloadTime: getFullNumber(unloadEventEnd - unloadEventStart),
      // Service worker time plus response time
      workerTime: workerStart > 0 ? getFullNumber(responseEnd - workerStart) : 0,
    };
  }

  getLegacyBaseEvents() {
    if (!this.state.isPerformanceEnabled) {
      return {};
    }

    const { timing } = performance;

    if (!timing) {
      return {};
    }

    const {
      connectEnd, connectStart, domainLookupEnd, domainLookupStart, domComplete,
      domContentLoadedEventEnd, domContentLoadedEventStart, domInteractive, fetchStart,
      loadEventEnd, navigationStart, requestStart, responseEnd, responseStart,
      secureConnectionStart,
    } = timing;

    const getFullNumber = this.getRoundedNumber;

    return {
      connectionTime: getFullNumber(connectEnd - connectStart),
      dnsTime: getFullNumber(domainLookupEnd - domainLookupStart),
      domComplete: getFullNumber(domComplete - navigationStart),
      domContentLoaded: getFullNumber(domContentLoadedEventEnd - domContentLoadedEventStart),
      domInteractive: getFullNumber(domInteractive - navigationStart),
      downloadTime: getFullNumber(responseEnd - responseStart),
      duration: getFullNumber(loadEventEnd - navigationStart),
      fetchStart: getFullNumber(fetchStart - navigationStart),
      fetchTime: getFullNumber(responseEnd - fetchStart),
      // check against navigation obj loaded
      loadEventEnd: getFullNumber(loadEventEnd),
      requestStart: getFullNumber(requestStart - navigationStart),
      secureConnectionTime: secureConnectionStart > 0 && (secureConnectionStart !== connectStart)
        ? getFullNumber(connectEnd - secureConnectionStart) : 0,
      totalNetworkTime: getFullNumber(responseEnd - requestStart),
      ttfb: getFullNumber(responseStart - requestStart),
    };
  }

  createResourceRecord(record) {
    const { name, startTime, duration } = record;
    return {
      name,
      startTime: this.getRoundedNumber(startTime),
      duration: this.getRoundedNumber(duration),
    };
  }

  getResourcesBeforeMark(mark) {
    const record = [];
    if (mark && this.state.isPerformanceEnabled) {
      const resources = performance.getEntriesByType('resource');
      let notFound = true;
      for (let cnt = 0; cnt < resources.length && notFound; cnt += 1) {
        if (
          (resources[cnt].startTime + resources[cnt].duration) >= mark.startTime
          || resources[cnt].startTime >= mark.startTime
        ) {
          notFound = false;
        } else {
          record.push(this.createResourceRecord(resources[cnt]));
        }
      }
    }
    return record;
  }

  // eslint-disable-next-line class-methods-use-this
  getResourcesSum(resources) {
    let sum = 0;
    for (const resource of resources) {
      sum += resource.duration;
    }
    return sum;
  }

  getLastMarkByName(markName) {
    let mark = 0;
    if (this.state.isPerformanceEnabled) {
      const marks = performance.getEntriesByName([markName]);
      if (Array.isArray(marks) && marks.length > 0) {
        mark = marks[marks.length - 1];
      }
    }
    return mark;
  }

  createPerformanceRecord(markNames, timings, resourceMarkName) {
    const performanceRecord = {
      timings, marks: [],
    };
    if (performance.hasOwnProperty('getEntriesByName') && performance.hasOwnProperty('getEntriesByType')) {
      for (const markName of markNames) {
        const mark = this.getLastMarkByName(markName);
        if (mark) {
          const { duration, name, startTime } = mark;
          performanceRecord.marks.push({
            name,
            startTime: this.getRoundedNumber(startTime),
            duration: this.getRoundedNumber(duration),
          });
        }
      }
      performanceRecord.resources = this.getResourcesBeforeMark(
        this.getLastMarkByName(resourceMarkName)
      );
      performanceRecord.resourceTotalDuration = this.getResourcesSum(
        performanceRecord.resources
      );
    }
    return performanceRecord;
  }

  createMark(markName) {
    if (this.state.isPerformanceEnabled && performance.hasOwnProperty('mark')) {
      performance.mark(markName);
      return true;
    }
    return false;
  }

  // Create Observer needs to be created when we have access to the brower resources
  trackPerformance(resourceMarkName, logFn) {
    if (this.state.isPerformanceEnabled) {
      /*
       * Chrome supports the observer, but Firefox fires the navigation event prematurely.
       * Safari supports the observer but is missing the core Performance Navigation
       * Only way to get the timings is to keep checking the object till its filled out...
       * Event readystatechange fires before the document.target.readystate=complete before
       * the navigation timings are complete
       */
      /* The below approach of interval is very similar to how google's script checks for timings.
       * They use setTimeout in a short circuit to pull this off.
       */
      const intervalId = setInterval(() => {
        const { hasPerformanceNavigation } = this.state;
        const timings = hasPerformanceNavigation
          ? this.getBaseEvents()
          : this.getLegacyBaseEvents();

        // need to validate the legacy deltas
        if (timings.loadEventEnd > 0) {
          clearInterval(intervalId);
          logFn(
            this.createPerformanceRecord(
              this.state.perfEventsRequired,
              timings,
              resourceMarkName
            )
          );
        }
      }, this.state.pollingInterval);
    }
  }
}

export default Performance;
